import { useEffect, useState } from 'react';
import { Modal, Radio } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useRealRegionList } from 'components/StoreSelector/hooks/useRegionList';
import { ApiQueryKey } from 'services/config/queryKey';
import { PriceZoneCode, zonePlanName } from '../priceZone';
import { ZoneScopeText } from './ZoneScopeText';

export interface ZoneChangeDialogProps {
  visible: boolean;
  /** 可购价区集合 */
  zones: PriceZoneCode[];
  currentZone: PriceZoneCode;
  onCancel: () => void;
  onConfirm: (zone: PriceZoneCode) => void;
}

/** 更换方案弹窗：radio 方案卡（副标题=站点范围）+ 已绑定站点块，确认后外层切区重拉目录 */
const ZoneChangeDialog = (props: ZoneChangeDialogProps) => {
  const { visible, zones, currentZone, onCancel, onConfirm } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const boundRegions = useRealRegionList();
  const queryClient = useQueryClient();

  // 打开弹窗时刷新已绑定站点（对齐 perseus enabled: open——授权店铺后 getStoreRegionInfo
  // 缓存可能已过期，Modal 常挂载不会自行 refetch）
  useEffect(() => {
    if (visible) {
      queryClient.invalidateQueries({ queryKey: [ApiQueryKey.StoreRegionInfo] });
    }
  }, [visible, queryClient]);

  // 打开时回当前方案；当前方案变化不覆盖进行中的选择
  const [draft, setDraft] = useState<PriceZoneCode>(currentZone);
  useEffect(() => {
    if (visible) setDraft(currentZone);
  }, [visible, currentZone]);

  return (
    <Modal
      visible={visible}
      title={t('更换方案')}
      width={514}
      okText={t('确认')}
      cancelText={t('translation.取消')}
      okButtonProps={{ disabled: draft === currentZone }}
      onOk={() => onConfirm(draft)}
      onCancel={onCancel}
    >
      <DialogContent>
        <div>
          <BlockLabel>{t('已绑定店铺站点')}</BlockLabel>
          <BoundSites>
            {boundRegions && boundRegions.length > 0
              ? boundRegions
                  .map((region) => getRegionLabel(region as General.CaselessRegion))
                  .join('、')
              : '—'}
          </BoundSites>
        </div>
        <div>
          <BlockLabel>{t('请选择方案')}</BlockLabel>
          <Radio.Group
            value={draft}
            onChange={(e) => setDraft(e.target.value as PriceZoneCode)}
            className="w-full"
          >
            {zones.map((zone) => (
              <ZoneCard
                key={zone}
                className={zone === draft ? 'checked' : ''}
                onClick={() => setDraft(zone)}
              >
                <Radio value={zone}>
                  <ZoneName>{zonePlanName(zone, t)}</ZoneName>
                </Radio>
                <ZoneScope>
                  <ZoneScopeText zone={zone} />
                </ZoneScope>
              </ZoneCard>
            ))}
          </Radio.Group>
        </div>
      </DialogContent>
    </Modal>
  );
};

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const BlockLabel = styled.div`
  margin-bottom: 4px;
  font-size: 13px;
  color: var(--fett-text-secondary);
`;
const BoundSites = styled.div`
  min-height: 32px;
  padding: 8px 12px;
  font-size: 13px;
  background: var(--fett-bg-panel);
  border-radius: 4px;
`;
const ZoneCard = styled.div`
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid var(--fett-border-strong);
  border-radius: 4px;
  cursor: pointer;

  &:hover,
  &.checked {
    border-color: var(--ant-primary-color);
  }
`;
const ZoneName = styled.span`
  font-size: 14px;
  font-weight: 500;
`;
const ZoneScope = styled.div`
  margin: 2px 0 0 24px;
  font-size: 12px;
  color: var(--fett-text-secondary);
`;

export default ZoneChangeDialog;
